import { ActorKind, SourcePredicate } from "@common/types/auditLog";
import {
    getSourcePredicatesForAgentKind,
    getSourcePredicatesForIntegration,
    IntegrationActorSelection,
} from "@common/helpers/historyActorPredicates";

type ActorPredicateInput =
    | { kind: ActorKind }
    | { integration: IntegrationActorSelection };

export function getSourcePredicatesForActor(
    input: ActorPredicateInput,
): SourcePredicate[] {
    if ("integration" in input) {
        return getSourcePredicatesForIntegration(input.integration);
    }
    return getSourcePredicatesForAgentKind(input.kind);
}

const sortedCopy = (
    values: readonly string[] | undefined,
): string[] | undefined =>
    values ? [...values].sort((a, b) => a.localeCompare(b)) : undefined;

function serializePredicate(predicate: SourcePredicate): string {
    return JSON.stringify({
        sourceTypes: sortedCopy(predicate.sourceTypes),
        resourceTypes: sortedCopy(predicate.resourceTypes),
        fieldsAllOf: sortedCopy(predicate.fieldsAllOf),
        fieldsSubsetOf: sortedCopy(predicate.fieldsSubsetOf),
        excludeFields: sortedCopy(predicate.excludeFields),
        changeTypes: sortedCopy(predicate.changeTypes),
        systemsAnyOf: sortedCopy(predicate.systemsAnyOf),
        systemsNoneOf: sortedCopy(predicate.systemsNoneOf),
    });
}

export function arePredicatesSubsetOf(
    candidate: SourcePredicate[],
    full: SourcePredicate[] | undefined,
): boolean {
    if (candidate.length === 0) {
        return false;
    }
    const fullKeys = new Set((full ?? []).map(serializePredicate));
    return candidate.every((p) => fullKeys.has(serializePredicate(p)));
}

/** The predicates that aren't one of `excluded`. */
export function withoutPredicates(
    predicates: SourcePredicate[] | undefined,
    excluded: SourcePredicate[],
): SourcePredicate[] {
    const excludedKeys = new Set(excluded.map(serializePredicate));
    return (predicates ?? []).filter(
        (predicate) => !excludedKeys.has(serializePredicate(predicate)),
    );
}

export function mergePredicates(
    predicateGroups: SourcePredicate[][],
): SourcePredicate[] {
    const seen = new Map<string, SourcePredicate>();
    for (const group of predicateGroups) {
        for (const predicate of group) {
            const key = serializePredicate(predicate);
            if (!seen.has(key)) {
                seen.set(key, predicate);
            }
        }
    }
    return [...seen.values()];
}
